import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminMailThreadDetail,
  getYowanApiFieldErrorMessage,
  updateYowanAdminMailThread,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const statusOptions = [
  { value: 'open', label: '対応中' },
  { value: 'pending', label: '返信待ち' },
  { value: 'closed', label: '完了' },
  { value: 'archived', label: 'アーカイブ' },
];

const statusLabels = Object.fromEntries(statusOptions.map((option) => [option.value, option.label]));

const getMailApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data || {};
  const errors = responseData.errors || responseData;
  const fieldMessage = getYowanApiFieldErrorMessage({ response: { data: errors } }, [
    'status',
    'non_field_errors',
    'detail',
  ]);

  return fieldMessage ? `${fallbackMessage} ${fieldMessage}` : fallbackMessage;
};

const AdminMailThreadStatusPage = () => {
  const content = yowanPageContent['/admin/mail/threads/:threadId/status'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const navigate = useNavigate();
  const { threadId } = useParams();
  const [thread, setThread] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');
    setMessage('');

    fetchYowanAdminMailThreadDetail(threadId)
      .then((payload) => {
        if (!isMounted) {
          return;
        }

        setThread(payload);
        setSelectedStatus(payload.status || 'open');
      })
      .catch(() => {
        if (isMounted) {
          setThread(null);
          setLoadError(content?.loadErrorMessage || 'メールスレッドを読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [content?.loadErrorMessage, threadId]);

  if (!content) {
    return null;
  }

  const detailHref = appendYowanPreviewQueryIfNeeded(`/admin/mail/threads/${threadId}`, preservedDomainSearch);
  const listHref = appendYowanPreviewQueryIfNeeded('/admin/mail', preservedDomainSearch);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!thread || !selectedStatus || selectedStatus === thread.status) {
      return;
    }

    setIsUpdating(true);
    setMessage(content.form.pendingMessage);

    try {
      await updateYowanAdminMailThread(thread.id, { status: selectedStatus });
      setMessage(content.form.updatedMessage);
      navigate(detailHref);
    } catch (error) {
      setMessage(getMailApiErrorMessage(error, content.form.errorMessage));
    } finally {
      setIsUpdating(false);
    }
  };

  if (!thread) {
    return (
      <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
        <AdminPageHeader
          title={content.header.title}
          paragraphs={isLoading ? [] : [loadError || content.notFoundMessage]}
          actions={[
            <Link key="back-to-mail" to={listHref}>
              一覧へ戻る
            </Link>,
          ]}
        />
        {isLoading ? <LoadingSpinner /> : null}
      </AdminBlogShell>
    );
  }

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-thread" to={detailHref}>
            スレッド詳細へ戻る
          </Link>,
          <Link key="back-to-mail" to={listHref}>
            一覧へ戻る
          </Link>,
        ]}
      />

      <section className="yowan-admin-mail-summary c-surface-content">
        <dl>
          <div>
            <dt>件名</dt>
            <dd>{thread.subject || '-'}</dd>
          </div>
          <div>
            <dt>相手</dt>
            <dd>{[thread.participantName, thread.participantEmail].filter(Boolean).join(' / ') || '-'}</dd>
          </div>
          <div>
            <dt>現在の状態</dt>
            <dd>{thread.needsResponse ? '要返信' : statusLabels[thread.status] || thread.status || '-'}</dd>
          </div>
          <div>
            <dt>最終更新</dt>
            <dd>{thread.latestMessageAt || '-'}</dd>
          </div>
        </dl>
      </section>

      <form className="yowan-admin-form c-surface-content" onSubmit={onSubmit}>
        <label className="c-form__field">
          <span className="c-form__label">{content.form.label}</span>
          <select
            className="c-form__control"
            value={selectedStatus}
            disabled={isUpdating}
            onChange={(event) => setSelectedStatus(event.target.value)}
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
            {content.form.helpText}
          </p>
        </label>
        <div className="yowan-admin-form__actions yowan-admin-form__actions--stacked">
          <button className="c-form__submit" type="submit" disabled={isUpdating || selectedStatus === thread.status}>
            {isUpdating ? content.form.pendingMessage : content.form.submitLabel}
          </button>
          {isUpdating || message ? (
            <div className="yowan-admin-form__action-feedback">
              {isUpdating ? <LoadingSpinner /> : null}
              {message ? <p className="yowan-admin-form__field-note">{message}</p> : null}
            </div>
          ) : null}
        </div>
      </form>
    </AdminBlogShell>
  );
};

export default AdminMailThreadStatusPage;
